import { CIMSColumnsOptions } from 'components/CIMSTable/CIMSColumn/types'
import { Issue } from '../../IssueTrackerContext/types'
import { CIMSPage, IssueStatus } from 'shared'
import { useStatus } from './hooks/useStatus'
import { useIssueTrackerContext } from '../../IssueTrackerContext'
import { CIMSTags } from 'components/CIMSTags'
import { useSingleUserColumn } from 'components/CIMSTable/common/hooks/useSingleUserColumn'
import { Tooltip } from 'antd'
import { CIMSColumnFilterItem } from 'components/CIMSTable/CIMSColumn/types/filter'
import { HelpMessage } from 'utils/constants'

export function useColumns(): CIMSColumnsOptions<Issue> {
  const {
    externalLinks: { externalLinks },
    edit,
  } = useIssueTrackerContext()
  const help = HelpMessage.Issues.IssueTracker
  return {
    columns: [
      useStatus(),
      {
        title: 'Item Number',
        render: 'formattedNumber',
        help: help.Number,
        width: 170,
        filter: { type: 'search', with: 'key', key: 'formattedNumber' },
        sort: {
          with: 'key',
          key: 'formattedNumber',
          default: 'ascend',
          priority: 1,
        },
      },
      externalLinks,
      {
        title: 'Types',
        help: help.Types,
        width: 200,
        link: CIMSPage.IssueTypes,
        render: ({ types }) => (
          <CIMSTags
            tags={types.map(({ name, description }) => ({
              text: name,
              tooltip: description,
            }))}
          />
        ),
        filter: {
          type: 'default',
          with: 'callback',
          items: (records) =>
            records.flatMap(({ types }) =>
              types.map(({ id, name, description }) => ({
                text: name,
                value: id,
                tooltip: description,
              }))
            ),
          onFilter: (value, { types }) =>
            types.some(({ id }) => id === value.toString()),
        },
      },
      {
        title: 'Description',
        help: help.Description,
        width: 400,
        render: 'description',
        filter: { type: 'search', with: 'key', key: 'description' },
      },
      useSingleUserColumn({
        title: 'Submitted By',
        help: help.SubmittedBy,
        user: ({ submittedBy }) => submittedBy,
      }),
      {
        title: 'Response',
        help: help.Response,
        width: 400,
        render: 'response',
        filter: { type: 'search', with: 'key', key: 'response' },
      },
      useSingleUserColumn({
        title: 'Response By',
        help: help.ResponseBy,
        user: ({ responseBy }) => responseBy,
      }),
      {
        title: 'Disposition',
        link: CIMSPage.IssueDispositionTypes,
        help: help.Disposition,
        width: 200,
        render: ({ dispositionType }) => (
          <Tooltip title={dispositionType?.description}>
            {dispositionType?.name}
          </Tooltip>
        ),
        filter: {
          type: 'default',
          with: 'callback',
          items: (records) =>
            records.reduce((items, { dispositionType }) => {
              if (!dispositionType) return items
              const { name, description, id } = dispositionType
              items.push({ text: name, tooltip: description, value: id })
              return items
            }, Array<CIMSColumnFilterItem>()),
          onFilter: (value, { dispositionType }) =>
            value === dispositionType?.id,
        },
      },
      {
        title: 'Disposition Notes',
        width: 400,
        help: help.DispositionNotes,
        render: 'dispositionNotes',
        filter: { type: 'search', with: 'key', key: 'dispositionNotes' },
      },
      useSingleUserColumn({
        title: 'Disposition By',
        help: help.DispositionBy,
        user: ({ dispositionBy }) => dispositionBy,
      }),
      {
        title: 'Closure Notes',
        width: 400,
        help: help.ClosureNotes,
        render: 'closureNotes',
        filter: { type: 'search', with: 'key', key: 'closureNotes' },
      },
      useSingleUserColumn({
        title: 'Closed By',
        help: help.ClosedBy,
        user: ({ closedBy }) => closedBy,
      }),
    ],
    prominentCustomFields: {
      onCheckBoolean: async ({ id }, input) =>
        edit({ id, customFields: { booleanInputs: [input] } }),
      onCheckBooleans: ({ id }, input) =>
        edit({ id, customFields: { listInputs: { checkboxes: [input] } } }),
      disableEditWhen: ({ statusId, deleted }) => ({
        alreadyDisabled:
          statusId === IssueStatus.Closed.id
            ? { why: 'invalid status', statusName: IssueStatus.Closed.name }
            : false,
        recordInactive: deleted,
      }),
    },
  }
}
